import { validateRedirectUrl } from './security'

export type ExternalOAuthProvider = 'google' | 'microsoft'

export interface ExternalOAuthLoginOptions {
  invitationToken?: string
  returnTo?: string
}

function continuationDomains(authServiceUrl: string, loginOrigin: string): string[] {
  const domains = new Set(['agion.dev', 'agion.cloud'])

  for (const rawURL of [authServiceUrl, loginOrigin]) {
    const hostname = new URL(rawURL).hostname.toLowerCase()
    if (hostname === 'localhost') {
      domains.add(hostname)
      continue
    }
    domains.add(hostname.replace(/^(auth|login)\./, ''))
  }

  return [...domains]
}

/**
 * Resolves the post-authentication handoff without creating a second OIDC
 * transaction. Auth Service owns /authorize; Login UI owns its local routes.
 */
export function resolveAuthenticationContinuation(
  returnTo: string,
  authServiceUrl: string,
  loginOrigin: string = window.location.origin,
): string {
  const safeReturnTo = validateRedirectUrl(
    returnTo,
    continuationDomains(authServiceUrl, loginOrigin),
  )
  if (safeReturnTo === '/' && returnTo !== '/') {
    throw new Error('Unsafe authentication continuation')
  }

  if (!safeReturnTo.startsWith('/')) {
    return safeReturnTo
  }

  const relativeURL = new URL(safeReturnTo, loginOrigin)
  const ownerOrigin = relativeURL.pathname === '/authorize' ? authServiceUrl : loginOrigin
  return new URL(
    `${relativeURL.pathname}${relativeURL.search}${relativeURL.hash}`,
    ownerOrigin,
  ).toString()
}

export function buildExternalOAuthLoginUrl(
  provider: ExternalOAuthProvider,
  authServiceUrl: string,
  options: ExternalOAuthLoginOptions = {},
): string {
  const oauthURL = new URL(`/oauth/${provider}/login`, authServiceUrl)
  // Auth Service stores invitation metadata in its server-side OAuth state.
  // Keep return_to last because its legacy parser also accepts unencoded OIDC
  // query strings by treating the remainder of the query as the continuation.
  if (options.invitationToken) {
    oauthURL.searchParams.set('invitation_token', options.invitationToken)
  }
  if (options.returnTo) {
    oauthURL.searchParams.set('return_to', options.returnTo)
  }
  return oauthURL.toString()
}
